/**
 * EventPady+ — OrgSwitcherContext
 *
 * Provides SUPER_ADMIN users with the ability to "view as" a specific
 * organisation from the platform dashboard.
 *
 * When a viewed org is selected:
 *   - The Axios request interceptor injects ?org_id=<uuid> on every request.
 *   - All data shown in the app is scoped to the selected org.
 *   - The OrgSwitcher dropdown in the header reflects the current selection.
 *
 * When no org is selected (viewedOrg === null):
 *   - The SUPER_ADMIN sees platform-level data (all orgs).
 *   - No ?org_id= is injected.
 *
 * This context is mounted at the DashboardLayout level and is only
 * meaningful for SUPER_ADMIN users. All other roles should never encounter it.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { apiClient } from '@/services/api/client'
import type { InternalAxiosRequestConfig } from 'axios'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ViewedOrg {
  id:   string
  name: string
  slug: string
}

interface OrgSwitcherState {
  /** The organisation currently being "viewed as", or null for all-org view. */
  viewedOrg: ViewedOrg | null
  /**
   * Switch to a specific org. Pass null to return to the platform-wide view.
   */
  setViewedOrg: (org: ViewedOrg | null) => void
  /** True when a specific org is being viewed (not the platform-wide view). */
  hasViewedOrg: boolean
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const OrgSwitcherContext = createContext<OrgSwitcherState>({
  viewedOrg:    null,
  setViewedOrg: () => undefined,
  hasViewedOrg: false,
})

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const ORG_SWITCHER_INTERCEPTOR_ID_KEY = '__orgSwitcherInterceptorId__'

export function OrgSwitcherProvider({ children }: { children: ReactNode }) {
  const [viewedOrg, setViewedOrgState] = useState<ViewedOrg | null>(null)

  /**
   * Update the viewed org and persist the Axios interceptor.
   * An interceptor is registered once and updated via a ref-like closure.
   */
  const setViewedOrg = useCallback((org: ViewedOrg | null) => {
    setViewedOrgState(org)
  }, [])

  /**
   * Register a request interceptor that injects ?org_id= whenever
   * a viewed org is active. We store the interceptor ID in a module-level
   * variable so we can eject and re-register when the org changes.
   */
  useEffect(() => {
    // Remove any previously registered interceptor
    const prevId = (window as unknown as Record<string, unknown>)[ORG_SWITCHER_INTERCEPTOR_ID_KEY] as number | undefined
    if (prevId !== undefined) {
      apiClient.interceptors.request.eject(prevId)
    }

    const interceptorId = apiClient.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (!viewedOrg) return config

        // Skip auth/refresh endpoints — they must not carry org_id
        const url = config.url ?? ''
        if (url.includes('/auth/') || url.includes('/platform-admin/')) {
          return config
        }

        // Inject ?org_id= into the query string
        const params = config.params ? { ...config.params } : {}
        params['org_id'] = viewedOrg.id
        config.params = params

        return config
      },
    )

    ;(window as unknown as Record<string, unknown>)[ORG_SWITCHER_INTERCEPTOR_ID_KEY] = interceptorId

    return () => {
      apiClient.interceptors.request.eject(interceptorId)
    }
  }, [viewedOrg])

  const value = useMemo<OrgSwitcherState>(
    () => ({
      viewedOrg,
      setViewedOrg,
      hasViewedOrg: viewedOrg !== null,
    }),
    [viewedOrg, setViewedOrg],
  )

  return (
    <OrgSwitcherContext.Provider value={value}>
      {children}
    </OrgSwitcherContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOrgSwitcher(): OrgSwitcherState {
  return useContext(OrgSwitcherContext)
}
